// types
import { Active, Over } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import create, { GetState, SetState } from "zustand";

//utils
import { MAX_NAME_LENGTH, ReviewQuestion, hasAllowedCalculatorTypes } from "./utils";

type EditFormStore = {
	questions: ReviewQuestion[];
	setQuestions: (questions: ReviewQuestion[]) => void;
	addQuestion: (question: ReviewQuestion, newIndex?: number) => void;
	deleteQuestion: (id: string) => void;
	reorderQuestions: (active: Active, over: Over | null) => void;
	editingQuestion: { refId?: string; isNew?: boolean } | null;
	setEditingQuestion: (refId?: string, isNew?: boolean) => void;
	/** Reads the open edit card's unsaved values; registered by the form builder while mounted. */
	activeQuestionValuesReader: (() => ReviewQuestion | null) | null;
	setActiveQuestionValuesReader: (reader: (() => ReviewQuestion | null) | null) => void;
	normalizeQuestions: (
		questions: ReviewQuestion[],
		editingQuestion?: ReviewQuestion | null
	) => { normalizedQuestions: ReviewQuestion[]; normalizedEdit: ReviewQuestion | null };
	name: string;
	setName: (name: string) => void;
	validateName: (originalName: string) => boolean;
	invalidNameMessage: string;
	hasChanges: boolean;
	setHasChanges: (hasChanges: boolean) => void;
	reset: () => void;
	resetForTesting: () => void;
	destroyForTesting: () => void;
};

export const useEditFormStore = create<EditFormStore>((set: SetState<EditFormStore>, get: GetState<EditFormStore>) => ({
	questions: [],

	setQuestions: (questions: ReviewQuestion[]) => set({ questions }),
	/**
	 *
	 * @param question
	 * @param newIndex
	 * this function is used to add a question to the questions array, if the question is being edited, it will save that question and create a new one at the given index
	 * if no index is provided, it will add the question to the end of the questions array
	 * note it DOES NOT validate the editing question and assumes it is a safe save, this can be ran as the on valid param in the validate function
	 */
	addQuestion: (question, newIndex = -1) => {
		const { questions } = get();
		const newQuestions = [...questions];

		// insert question at the specified index, or at the end if no index is provided
		if (newIndex === -1) {
			newQuestions.push(question);
		} else {
			newQuestions.splice(newIndex, 0, question);
		}

		set({ questions: newQuestions, hasChanges: true });
	},

	deleteQuestion: (id: string) => {
		const { questions, editingQuestion } = get();
		const updatedQuestions = questions.filter((q) => q.refId !== id);

		// If we're deleting the currently editing question, clear the edit state
		if (editingQuestion?.refId === id) {
			set({ hasChanges: true, questions: updatedQuestions, editingQuestion: null });
		} else {
			// Otherwise, preserve the current editing question if it exists
			const currentEditingQuestion = editingQuestion?.refId
				? questions.find((q) => q.refId === editingQuestion.refId)
				: null;
			const { normalizedQuestions, normalizedEdit } = get().normalizeQuestions(
				updatedQuestions,
				currentEditingQuestion || undefined
			);
			set({
				hasChanges: true,
				questions: normalizedQuestions,
				editingQuestion: normalizedEdit ? { refId: normalizedEdit.refId, isNew: editingQuestion?.isNew } : null,
			});
		}
	},
	reorderQuestions: (active: Active, over: Over | null) => {
		const { questions } = get();
		const oldIndex = questions.findIndex((q) => q.refId === active.id);
		const newIndex = questions?.findIndex((q) => q.refId === over?.id);

		const newArray = arrayMove(questions, oldIndex, newIndex);
		set({ questions: newArray, hasChanges: true });
	},

	editingQuestion: null,
	setEditingQuestion: (refId?: string, isNew = false) => {
		if (refId) {
			set({ editingQuestion: { refId, isNew } });
		} else {
			set({ editingQuestion: null });
		}
	},

	activeQuestionValuesReader: null,
	setActiveQuestionValuesReader: (reader) => set({ activeQuestionValuesReader: reader }),
	normalizeQuestions: (questions, editingQuestion) => {
		// here we parse through the questions with sub-quesitons and remove any with a refId that no longer exists, or detect if the type of a given ref is invalid due to a type change
		const refIdMap = questions.reduce((acc, question) => {
			acc[question.refId] = question;
			return acc;
		}, {});

		//if editing question is null, we are not editing a question, so we can just remove any sub-questions that are no longer valid
		//else replace editing question with the new question
		if (editingQuestion) {
			if (editingQuestion?.questions) {
				const newSubQuestions = editingQuestion.questions.filter(
					(subQ) => refIdMap[subQ.refId] && hasAllowedCalculatorTypes(refIdMap[subQ.refId])
				);
				editingQuestion.questions = newSubQuestions;
			}
			refIdMap[editingQuestion.refId] = editingQuestion;
		}

		const newQuestions = questions.map((q) => {
			const currentQuestion = q;
			if (currentQuestion?.questions && currentQuestion.questions?.length > 0) {
				const newSubQuestions = currentQuestion.questions.filter(
					(subQ) => refIdMap[subQ.refId] && hasAllowedCalculatorTypes(refIdMap[subQ.refId])
				);
				return { ...currentQuestion, questions: newSubQuestions };
			} else {
				return currentQuestion;
			}
		});

		return {
			normalizedQuestions: newQuestions,
			normalizedEdit: editingQuestion?.refId ? refIdMap[editingQuestion?.refId] : null,
		};
	},

	name: "",
	setName: (name: string) => set({ name, hasChanges: true }),

	invalidNameMessage: "",

	hasChanges: false,
	setHasChanges: (hasChanges: boolean) => set({ hasChanges }),

	//validate that the name is not empty and that it is not longer than the max name length
	validateName: (originalName) => {
		const { name, setName } = get();
		const trimmedName = name.trim();
		if (trimmedName === "") {
			setName(originalName);
			return false;
		} else if (trimmedName.length > MAX_NAME_LENGTH) {
			set({ invalidNameMessage: `Name has maximum length of ${MAX_NAME_LENGTH} characters` });
			return false;
		}
		set({ invalidNameMessage: "" });
		return true;
	},

	reset: () => {
		set({
			questions: [],
			editingQuestion: null,
			name: "",
			invalidNameMessage: "",
			hasChanges: false,
		});
	},

	// More thorough reset for testing
	resetForTesting: () => {
		// Clear all state completely
		set({
			questions: [],
			editingQuestion: null,
			name: "",
			invalidNameMessage: "",
			hasChanges: false,
		});

		// Force a complete state reset by creating a new state object
		set((state) => ({
			...state,
			questions: [],
			editingQuestion: null,
			name: "",
			invalidNameMessage: "",
			hasChanges: false,
		}));
	},

	// Complete store destruction for testing
	destroyForTesting: () => {
		// Clear all state and unsubscribe all listeners
		set({
			questions: [],
			editingQuestion: null,
			name: "",
			invalidNameMessage: "",
			hasChanges: false,
		});

		// Force unsubscribe of all listeners if destroy method exists
		if (typeof useEditFormStore.destroy === "function") {
			useEditFormStore.destroy();
		}
	},
}));
